import { useContext, useLayoutEffect, useRef, useState } from "react";
import {
  BotIcon,
  CarIcon,
  ClockIcon,
  GlobeIcon,
  MessageSquareIcon,
  PencilIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  type LucideIcon,
} from "lucide-react";

import { useUserSheet } from "./userSheetContext";
import type { UserSheetMetric, UserSheetMetricValues } from "./types";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { EmailDropdown } from "@ui/contact/EmailDropdown";
import { MobileAppDropdown } from "@ui/contact/MobileAppDropdown";
import { PhoneDropdown } from "@ui/contact/PhoneDropdown";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { SheetScopeContext } from "@/contexts/SheetScopeContext";
import { useTaggableUsers } from "@/contexts/TaggableUsersContext";

import { useUpdateProfileUser } from "@/hooks/mutations/useUpdateProfileUser";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useCurrentJobResponse } from "@/hooks/queries/useCurrentJobResponse";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { userSheetViewStore } from "@/stores/userSheetView";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { MS_PER_MINUTE } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { locations } from "@/lib/locations";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { CandidacySource, CompensationType } from "@/types";

const METRIC_CHIP_CLASSNAME =
  "rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-normal text-slate-500";
const SECONDARY_METRIC_CHIP_MIN_WIDTH_PX = 900;
const PAY_COMPARISON_CHIP_MIN_WIDTH_PX = 700;
// Pseudo-users that front the AI agents — they own AI comms, never human ones.
const agentUserIdToName: Record<number, string> = {
  49: "Sally",
  52: "Stevie",
};

// The contact row and metric row that sit under the identity header. Rendered
// inside the tab pane's scroll container so they scroll away naturally under
// the sticky identity row instead of animating closed.
export const UserSheetHeaderDetails = ({
  metricValues = {},
}: {
  metricValues?: UserSheetMetricValues;
}) => {
  /* Context */
  const {
    candidacyId,
    isCommunicationsOpen,
    isReadOnly,
    jobId,
    onEditProfile,
    openCommunications,
    workerId,
  } = useUserSheet();
  const { userNameMap, users } = useTaggableUsers();
  const sheetScopeContext = useContext(SheetScopeContext);

  /* Database CRUD Operations */
  // Read
  const currentUser = useGetCurrentUser();
  const currentJobResponseApi = useCurrentJobResponse();
  const profileApi = useGetCandidacySheetData({ jobId, userId: workerId });
  const profile = profileApi.data;
  const isCommuteRefreshing = userSheetViewStore((s) => s.isCommuteRefreshing);
  const candidacyInfoQuery = useGetCandidacyInfo({
    candidacyId: candidacyId ?? "",
  });
  const candidacyInfo = candidacyInfoQuery.data;
  // Update
  const { mutate: updateProfileUser, isPending: isUpdatingProfileUser } =
    useUpdateProfileUser();

  /* React Hooks */
  const [metricsContainerWidth, setMetricsContainerWidth] = useState(0);
  const [editingPayField, setEditingPayField] = useState<
    "currentPay" | "desiredPay"
  >();
  const [payDraft, setPayDraft] = useState("");
  const metricsContainerRef = useRef<HTMLDivElement>(null);
  const shouldCancelPaySaveRef = useRef(false);

  useLayoutEffect(() => {
    const metricsContainer = metricsContainerRef.current;
    if (!metricsContainer) return;

    const measureMetricsContainer = () => {
      setMetricsContainerWidth(metricsContainer.clientWidth);
    };

    measureMetricsContainer();
    const resizeObserver = new ResizeObserver(measureMetricsContainer);
    resizeObserver.observe(metricsContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /* Helpers */
  // The comms rail pushes the header in; tab content shifts by a different
  // amount, so this is derived here rather than shared through context.
  const headerPaddingClassName = isCommunicationsOpen ? "pl-10 pr-6" : "px-6";
  const appDownloadUrl = currentUser
    ? misc.generateAppDownloadUrl(workerId, currentUser.id)
    : undefined;
  const currentUserQuoPhoneNumberId =
    users.find((user) => user.id === currentUser?.id)?.quoPhoneNumberId ?? null;
  const quoConversationId = candidacyInfo?.quoConversationId;
  // Candidacy-backed sheets show the candidacy source; the search sheet falls
  // back to the user's source.
  const source = candidacyInfo?.source
    ? formatting.candidacySourceLabel(candidacyInfo.source)
    : profile?.source;
  const shouldShowSecondaryMetricChips =
    metricsContainerWidth >= SECONDARY_METRIC_CHIP_MIN_WIDTH_PX;
  const shouldShowPayComparisonChips =
    metricsContainerWidth >= PAY_COMPARISON_CHIP_MIN_WIDTH_PX;
  const profileLocation = profile?.profile.location
    ? locations.stripCountrySuffix(profile.profile.location)
    : null;

  // Every sheet shows the same metric row, so this component owns it instead
  // of each consumer assembling and passing its own.
  const currentJob = sheetScopeContext?.job ?? currentJobResponseApi.data?.job;
  const jobCompensationType = currentJob?.compensationType ?? null;
  const isHourlyJob = jobCompensationType === CompensationType.HOURLY;
  const offeredPay = jobCompensationType
    ? (currentJob?.compensationMax ?? null)
    : null;

  const { commuteMinutes, lastAiCommunication, lastHumanCommunication } =
    metricValues;
  const humanCommsStafferId = lastHumanCommunication?.stafferId;
  const humanCommsUserName = humanCommsStafferId
    ? (userNameMap[humanCommsStafferId] ?? null)
    : null;
  const aiCommsUserName = lastAiCommunication?.stafferId
    ? (agentUserIdToName[lastAiCommunication.stafferId] ?? null)
    : null;
  const metrics: UserSheetMetric[] = [
    {
      chip: profileLocation,
      Icon: CarIcon,
      isLoading: isCommuteRefreshing || candidacyInfoQuery.isFetching,
      key: "commute",
      kind: "value",
      label: "Commute time",
      priority: "secondary",
      value:
        commuteMinutes != null
          ? dates.formatCountdown(Math.round(commuteMinutes) * MS_PER_MINUTE)
          : "—",
    },
    // Pay comparisons need something to compare against — hide otherwise.
    ...(offeredPay !== null
      ? ([
          {
            comparePay: offeredPay,
            key: "current-pay",
            kind: "pay",
            label: "Current Pay",
            pay: profile?.profile.currentPay ?? null,
            priority: "primary",
          },
          {
            comparePay: offeredPay,
            key: "desired-pay",
            kind: "pay",
            label: "Desired Pay",
            pay: profile?.profile.desiredPay ?? null,
            priority: "primary",
          },
        ] satisfies UserSheetMetric[])
      : []),
    {
      chip: aiCommsUserName,
      Icon: BotIcon,
      key: "ai-comms",
      kind: "value",
      label: "AI comms",
      priority: "secondary",
      value: lastAiCommunication?.at
        ? dates.formatShortDate(lastAiCommunication.at)
        : "—",
    },
    {
      chip: humanCommsUserName,
      Icon: MessageSquareIcon,
      key: "human-comms",
      kind: "value",
      label: "Human comms",
      priority: "secondary",
      value: lastHumanCommunication?.at
        ? dates.formatShortDate(lastHumanCommunication.at)
        : "—",
    },
  ];

  const getPayDifference = (pay: number | null, comparePay: number | null) => {
    if (pay === null || comparePay === null) return undefined;
    return comparePay - pay;
  };

  // Hourly amounts need cents to stay meaningful; yearly ones don't.
  const formatPayAmount = (amount: number) =>
    isHourlyJob
      ? `$${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
      : formatting.currency(amount);

  const formatPayDifference = (difference: number) => {
    const sign = difference > 0 ? "+" : difference < 0 ? "-" : "";
    return `${sign}${formatPayAmount(Math.abs(difference))}`;
  };

  // Trend direction follows the sign of the difference, not the metric itself.
  const getTrendTone = (difference: number | undefined) => {
    if (difference !== undefined && difference > 0) {
      return {
        comparisonClassName: "bg-green-50 text-green-700",
        TrendIcon: TrendingUpIcon as LucideIcon,
        trendClassName: "text-green-700",
      };
    }
    if (difference !== undefined && difference < 0) {
      return {
        comparisonClassName: "bg-red-50 text-red-700",
        TrendIcon: TrendingDownIcon as LucideIcon,
        trendClassName: "text-red-700",
      };
    }
    return {
      comparisonClassName: "bg-slate-50 text-slate-600",
      TrendIcon: TrendingUpIcon as LucideIcon,
      trendClassName: "text-slate-400",
    };
  };

  /* Handlers */
  // Open the stored Quo conversation when there is one; otherwise copy the
  // phone number and land on the staffer's Quo inbox.
  const handleCallInQuo = () => {
    if (!currentUserQuoPhoneNumberId) return;
    mixpanelUtils.trackButtonClick(
      "Open in Quo",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    if (quoConversationId) {
      window.open(
        `https://my.quo.com/inbox/${currentUserQuoPhoneNumberId}/c/${quoConversationId}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    if (profile?.profile.phone) {
      void navigator.clipboard.writeText(profile.profile.phone);
    }
    window.open(
      `https://my.quo.com/inbox/${currentUserQuoPhoneNumberId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleStartPayEdit = (
    field: "currentPay" | "desiredPay",
    pay: number | null,
  ) => {
    shouldCancelPaySaveRef.current = false;
    setEditingPayField(field);
    setPayDraft(pay === null ? "" : String(pay));
  };

  const handleCancelPayEdit = () => {
    shouldCancelPaySaveRef.current = true;
    setEditingPayField(undefined);
    setPayDraft("");
  };

  const handleSavePayEdit = () => {
    if (shouldCancelPaySaveRef.current) {
      shouldCancelPaySaveRef.current = false;
      return;
    }
    if (!editingPayField || !currentUser) return;
    if (isUpdatingProfileUser) return;

    // Leaving the field empty just closes the editor and keeps the pay unset.
    const trimmedPay = payDraft.trim();
    if (!trimmedPay) {
      handleCancelPayEdit();
      return;
    }
    const numericPay = Number(trimmedPay);
    if (!Number.isFinite(numericPay) || numericPay < 0) return;

    const data =
      editingPayField === "currentPay"
        ? { currentPay: numericPay }
        : { desiredPay: numericPay };
    const handleSuccess = () => {
      handleCancelPayEdit();
      void profileApi.refetch();
    };

    updateProfileUser(
      {
        data,
        stafferId: currentUser.id,
        userId: workerId,
      },
      { onSuccess: handleSuccess },
    );
  };

  /* Render */
  const renderValueMetric = (
    metric: Extract<UserSheetMetric, { kind: "value" }>,
  ) => {
    const { chip, Icon, isLoading, label, priority, value } = metric;

    return (
      <Column className="shrink-0 gap-2" key={metric.key}>
        <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-500">
          {label}
        </Text>
        <Row className="items-center gap-2">
          <Icon
            className={cn(
              "h-4 w-4 shrink-0 text-slate-400",
              isLoading && "animate-pulse",
            )}
            strokeWidth={1.5}
          />
          <Text
            className={cn(
              "text-base font-medium text-slate-800",
              isLoading && "animate-pulse text-slate-400",
            )}
          >
            {isLoading ? "Updating…" : value}
          </Text>
          {chip ? (
            <Text
              className={cn(
                METRIC_CHIP_CLASSNAME,
                priority === "secondary" &&
                  !shouldShowSecondaryMetricChips &&
                  "hidden",
              )}
            >
              {chip}
            </Text>
          ) : null}
        </Row>
      </Column>
    );
  };

  const renderPayMetric = (
    metric: Extract<UserSheetMetric, { kind: "pay" }>,
  ) => {
    const { comparePay, label, pay } = metric;
    // Pay renders as the candidate stated it. A trend against the job's rate
    // only means something when both figures share a unit.
    const hasComparableUnits =
      pay !== null && formatting.payIsHourly(pay) === isHourlyJob;
    const difference = hasComparableUnits
      ? getPayDifference(pay, comparePay)
      : undefined;
    const shouldShowTrend = pay === null || hasComparableUnits;
    const { comparisonClassName, TrendIcon, trendClassName } =
      getTrendTone(difference);
    const field = metric.key === "current-pay" ? "currentPay" : "desiredPay";
    const isEditing = editingPayField === field;
    const sourcePayDisplay = pay === null ? "—" : formatting.payWithUnit(pay);

    const renderComparisonChip = () => {
      if (difference === undefined || !shouldShowPayComparisonChips)
        return null;

      return (
        <Row
          className={cn(
            "items-center gap-1 rounded px-1.5 py-0.5",
            comparisonClassName,
          )}
        >
          <TrendIcon className="h-3 w-3 shrink-0" strokeWidth={2} />
          <Text className="text-[11px] font-normal text-inherit">
            {formatPayDifference(difference)}
          </Text>
        </Row>
      );
    };

    const renderPayValue = () => (
      <>
        {shouldShowTrend ? (
          <TrendIcon
            className={cn("h-4 w-4 shrink-0", trendClassName)}
            strokeWidth={2}
          />
        ) : null}
        <Text className="text-base font-medium text-slate-800">
          {sourcePayDisplay}
        </Text>
        {renderComparisonChip()}
      </>
    );

    return (
      <Column className="shrink-0 gap-2" key={metric.key}>
        <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-500">
          {label}
        </Text>
        <Row className="items-center gap-2">
          {isEditing ? (
            <Row className="h-6 items-center gap-2">
              <TrendIcon
                className={cn("h-4 w-4 shrink-0", trendClassName)}
                strokeWidth={2}
              />
              <Row className="relative items-center">
                <Text className="pointer-events-none absolute left-2 text-sm font-normal text-slate-500">
                  $
                </Text>
                <Input
                  autoFocus
                  className="h-6 w-24 appearance-none pl-5 pr-2 text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  disabled={isUpdatingProfileUser}
                  onBlur={handleSavePayEdit}
                  onChange={(event) => {
                    setPayDraft(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleSavePayEdit();
                    if (event.key === "Escape") handleCancelPayEdit();
                  }}
                  type="number"
                  value={payDraft}
                />
              </Row>
              <Text className="text-sm font-normal text-slate-700">
                {formatting.payIsHourly(Number(payDraft)) ? "/hr" : "/yr"}
              </Text>
            </Row>
          ) : onEditProfile ? (
            <Button
              className="group/pay h-auto gap-2 rounded-md p-0 hover:bg-transparent"
              onClick={() => {
                handleStartPayEdit(field, pay);
              }}
              type="button"
              variant="ghost"
            >
              {shouldShowTrend ? (
                <TrendIcon
                  className={cn(
                    "h-4 w-4 shrink-0 group-hover/pay:hidden",
                    trendClassName,
                  )}
                  strokeWidth={2}
                />
              ) : null}
              <PencilIcon
                className="hidden h-4 w-4 shrink-0 text-slate-950 group-hover/pay:block"
                strokeWidth={1.5}
              />
              <Text className="text-base font-medium text-slate-800">
                {sourcePayDisplay}
              </Text>
              {renderComparisonChip()}
            </Button>
          ) : (
            renderPayValue()
          )}
        </Row>
      </Column>
    );
  };

  const renderMetrics = () => {
    return (
      <Row
        className="w-full flex-nowrap items-start justify-start gap-x-[clamp(0.5rem,2.5vw,2.5rem)] pb-9 pl-2 pt-4"
        ref={metricsContainerRef}
      >
        {metrics.map((metric) =>
          metric.kind === "pay"
            ? renderPayMetric(metric)
            : renderValueMetric(metric),
        )}
      </Row>
    );
  };

  const renderSource = () => {
    if (!source) return null;

    const sourceContent = (
      <Row className="shrink-0 items-center gap-2">
        <GlobeIcon
          className="h-4 w-4 shrink-0 text-slate-400"
          strokeWidth={1.5}
        />
        <Text className="text-xs font-normal text-slate-500">{source}</Text>
      </Row>
    );

    if (candidacyInfo?.source !== CandidacySource.INDEED) {
      return sourceContent;
    }
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const indeedJobPost = candidacyInfo.atsJobTitleAndLocation || "—";

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{sourceContent}</TooltipTrigger>
          <TooltipContent className="max-w-72 rounded-lg border-slate-200 bg-white px-3 py-2 text-slate-900 shadow-md">
            <Column className="items-start gap-1">
              <Text className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                Indeed application
              </Text>
              <Text className="text-xs font-normal text-slate-700">
                Applied{" "}
                {dates.formatIsoDateForUser(candidacyInfo.createdAt) ?? "—"}
              </Text>
              <Text className="text-xs font-normal text-slate-700">
                Job post: {indeedJobPost}
              </Text>
            </Column>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderCandidacyCreatedAt = () => {
    const createdAt = candidacyInfo?.createdAt;
    if (!createdAt) return null;

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Row className="shrink-0 items-center gap-2">
              <ClockIcon
                className="h-4 w-4 shrink-0 text-slate-400"
                strokeWidth={1.5}
              />
              <Text className="text-xs font-normal text-slate-500">
                {dates.formatNumericDate(createdAt)}
              </Text>
            </Row>
          </TooltipTrigger>
          <TooltipContent>
            <Text className="text-sm font-normal text-slate-950">
              When this candidacy was created
            </Text>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderContactRow = () => {
    return (
      <Row
        // 52px avatar + 24px gap so contacts align under the name column.
        className="w-full min-w-0 flex-wrap items-center gap-x-6 gap-y-2 pb-4 pl-[76px]"
      >
        <EmailDropdown
          email={profile?.email ?? ""}
          isReadOnly={isReadOnly}
          onAddEmail={onEditProfile}
          onSendEmail={() => {
            openCommunications("email");
          }}
        />
        <PhoneDropdown
          isReadOnly={isReadOnly}
          onCallInQuo={
            currentUserQuoPhoneNumberId ? handleCallInQuo : undefined
          }
          onMessageCandidate={() => {
            openCommunications("messages");
          }}
          phone={profile?.profile.phone ?? ""}
        />
        <Row className="flex-wrap items-center gap-x-6 gap-y-2">
          {renderSource()}
          <MobileAppDropdown
            appDownloadUrl={appDownloadUrl}
            hasSignedUp={!!profile && !profile.isUnregistered}
            isReadOnly={isReadOnly}
          />
          {renderCandidacyCreatedAt()}
        </Row>
      </Row>
    );
  };

  const render = () => {
    return (
      <Column className={cn("shrink-0", headerPaddingClassName)}>
        {renderContactRow()}
        {renderMetrics()}
      </Column>
    );
  };

  return render();
};
